/* eslint-disable quotes */

export function buildQuestionLangs(langs = []) {
  const o = {};
  langs.forEach(({ key }) => {
    o[key] = { lang: key, id: null, question: "", feedback: "" };
  });
  return o;
}

export function buildAnswerLangs(langs = []) {
  const o = {};
  langs.forEach(({ key }) => {
    o[key] = { lang: key, id: null, answer: "" };
  });
  return o;
}

export function buildQuestionValidations() {
  return { question: "", feedback: "", minAnswer: "" };
}

export function buildAnswerValidations() {
  return { answer: "" };
}

/**
 * Normaliza y asegura la forma mínima del modelo.
 */
export function ensureShape(model = {}, langs = [], { minAnswers = 4 } = {}) {
  const c = JSON.parse(JSON.stringify(model || {}));
  c.difficulty_level = Number(c.difficulty_level) || 1;
  c.validations = c.validations || buildQuestionValidations();
  c.langs = c.langs || buildQuestionLangs(langs);

  // Asegura presencia de todas las keys de idioma
  langs.forEach(({ key }) => {
    if (!c.langs[key]) {
      c.langs[key] = { lang: key, id: null, question: "", feedback: "" };
    }
  });

  c.answers = Array.isArray(c.answers) ? c.answers : [];
  if (c.answers.length === 0) {
    for (let i = 0; i < minAnswers; i++) {
      c.answers.push({
        id: null,
        ilongame_question_id: c.id || null,
        is_correct: false,
        disabled: false,
        langs: buildAnswerLangs(langs),
        validations: buildAnswerValidations(),
      });
    }
  } else {
    c.answers = c.answers.map((a) => {
      const ans = { ...a };
      ans.disabled = Boolean(ans.disabled);
      ans.is_correct = Boolean(ans.is_correct);
      ans.validations = ans.validations || buildAnswerValidations();
      ans.langs = ans.langs || {};
      langs.forEach(({ key }) => {
        if (!ans.langs[key]) {
          ans.langs[key] = { lang: key, id: null, answer: "" };
        }
      });
      return ans;
    });
  }
  return c;
}

/**
 * Valida y actualiza mensajes de error. Devuelve true si TODO es válido.
 * Requiere: langs, minAnswers, y una función t para traducciones.
 */
export function validateQnA(model, langs = [], { minAnswers = 4, t = (k) => k } = {}) {
  // Reset
  model.validations = buildQuestionValidations();
  model.answers.forEach((a) => {
    a.validations = buildAnswerValidations();
  });

  const langsKeysQuestion = [];
  const langsKeysFeedback = [];

  langs.forEach(({ key }) => {
    const data = model.langs[key] || {};
    if (!data.question || data.question.trim() === "") {
      langsKeysQuestion.push(key);
    }
    if (!data.feedback || data.feedback.trim() === "") {
      langsKeysFeedback.push(key);
    }
  });

  // Mensajes (usa t del padre)
  if (langsKeysQuestion.length > 0) {
    const names = langsKeysQuestion.map((k) => t(`components.langs.${k}.name`)).join(", ");
    model.validations.question = `${t("views.ilongame.questionsWithAnswers.form.question.validation")} (${names})`;
  }

  // feedback opcional — en tu versión original: si faltan algunos pero no todos
  if (langsKeysFeedback.length > 0 && langsKeysFeedback.length < langs.length) {
    const names = langsKeysFeedback.map((k) => t(`components.langs.${k}.name`)).join(", ");
    model.validations.feedback = `${t("views.ilongame.questionsWithAnswers.form.feedback.validation")} (${names})`;
  }

  // Al menos una correcta
  if (model.answers.filter((a) => !a.disabled && a.is_correct).length < 1) {
    model.validations.minAnswer = t("views.ilongame.questionsWithAnswers.form.answer.min_correct_validation");
  }
  // Mínimo de respuestas activas
  if (model.answers.filter((a) => !a.disabled).length < minAnswers) {
    model.validations.minAnswer = t("views.ilongame.questionsWithAnswers.form.answer.min_validation", {
      min: minAnswers,
    });
  }

  // Cada respuesta por idioma
  const textAnswer = t("views.ilongame.questionsWithAnswers.form.answer.validation");
  model.answers.forEach((a) => {
    if (a.disabled) return;
    const missing = [];
    langs.forEach(({ key }) => {
      const d = a.langs[key] || {};
      if (!d.answer || d.answer.trim() === "") missing.push(key);
    });
    if (missing.length > 0) {
      const names = missing.map((k) => t(`components.langs.${k}.name`)).join(", ");
      a.validations = { answer: `${textAnswer} (${names})` };
    }
  });

  // Helper de “sin errores”
  const isEmpty = (src = {}) => Object.values(src).every((v) => !v || String(v).trim() === "");
  const okQuestion = isEmpty(model.validations);
  const okAnswers = model.answers.every((a) => isEmpty(a.validations));
  return okQuestion && okAnswers;
}

/**
 * Formatea para enviar al backend.
 */
export function formatToSend(data = {}) {
  const editMode = !!data.id;
  const out = {
    id: data.id || null,
    mini_guide_id: data.mini_guide_id || null,
    difficulty_level: Number(data.difficulty_level) || 1,
    langs: Object.values(data.langs || {}).map((l) => ({
      id: l.id || null,
      ilongame_question_id: data.id || null,
      lang: l.lang || null,
      question: l.question || null,
      feedback: l.feedback || null,
    })),
    answers: (data.answers || [])
      .filter((a) => !a.disabled)
      .map((a) => ({
        id: a.id || null,
        ilongame_question_id: data.id || null,
        is_correct: !!a.is_correct,
        langs: Object.values(a.langs || {}).map((l) => ({
          id: l.id || null,
          ilongame_answer_id: a.id || null,
          lang: l.lang || null,
          answer: l.answer || null,
        })),
      })),
  };
  return [out, editMode];
}

/**
 * Parseo desde API a estructura interna.
 */
export function parseFromApi(data = {}, langs = []) {
  const model = {
    id: data.id || null,
    mini_guide_id: data.mini_guide_id || null,
    difficulty_level: data.difficulty_level || 1,
    langs: buildQuestionLangs(langs),
    validations: buildQuestionValidations(),
    answers: [],
  };

  // Idiomas de la pregunta
  (data.langs || []).forEach((langObj) => {
    const k = langObj.lang;
    if (!k || !model.langs[k]) return;
    model.langs[k] = {
      lang: k,
      id: langObj.id || null,
      question: langObj.question || "",
      feedback: langObj.feedback || "",
    };
  });

  // Respuestas
  (data.answers || []).forEach((answerObj) => {
    const ansLangs = buildAnswerLangs(langs);
    (answerObj.langs || []).forEach((l) => {
      const k = l.lang;
      if (!k || !ansLangs[k]) return;
      ansLangs[k] = { lang: k, id: l.id || null, answer: l.answer || "" };
    });
    model.answers.push({
      id: answerObj.id || null,
      ilongame_question_id: answerObj.ilongame_question_id || null,
      is_correct: !!answerObj.is_correct,
      disabled: false,
      langs: ansLangs,
      validations: buildAnswerValidations(),
    });
  });

  return model;
}
